import React, {useState} from 'react';
import styled from 'styled-components';
import {Field} from 'react-final-form';
import classNames from 'classnames';
import CustomSelect from '../formElements/inputFields/CustomSelect';
import InputValidationErrorMsg from '../formElements/inputFields/InputValidationErrorMsg';
import {RemoveIcon, VerticalSpacer} from 'epic-ue-ui';
import InputLabel from '../formElements/inputFields/InputLabel';
import {required, validMonth, validYear} from '../jobForm.utils';
import {withLocalization} from '@epic-core/localization';

const Styled = styled.div`
    max-width: 100%;
    .field-group {
        margin-bottom: 15px;

        &.spacer-div {
            width: 500px;
        }
    }

    .remove-education-block {
        font-size: 40px;
        line-height: 32px;
        width: 100%;
        text-align: right;
        transition: color 0.25s ${(p) => p.theme.easing}, opacity 0.25s ${(p) => p.theme.easing},
            transform 0.25s ${(p) => p.theme.easing};

        &:hover {
            opacity: 0.9;
        }
    }
`;

export interface EducationFormFieldsBlockProps {
    educationBlockIndex: number;
    schools: any[];
    degrees: any[];
    disciplines: any[];
    educationRequired?: boolean;
    pleaseSelectLabel?: string;
    handleSchoolScroll?: (e: any) => void;
    handleSchoolSearch?: (e: any) => void;
    removeEducationBlock?: (e: any) => void;
    isDisabled?: boolean;
    sectionRequiredLabel?: string;
    jobType?: string;
    form?: any;
    getMessage: (key: string) => string;
}

const EducationFormFieldsBlock = ({
    getMessage,
    educationBlockIndex,
    schools,
    degrees,
    disciplines,
    pleaseSelectLabel,
    handleSchoolScroll,
    handleSchoolSearch,
    isDisabled = false,
    sectionRequiredLabel,
    form,
    jobType
}: EducationFormFieldsBlockProps): JSX.Element => {
    const [hideCurrentBlock, setHideCurrentBlock] = useState(false);
    const isIntern = jobType === 'Intern';

    return (
        <Styled className="education-form-fields">
            {!hideCurrentBlock && (
                <>
                    {' '}
                    {educationBlockIndex > 0 && (
                        <div
                            role="button"
                            tabIndex={0}
                            className="remove-education-block"
                            onClick={() => {
                                setHideCurrentBlock(true);
                                form.change(`educations[${educationBlockIndex}]`, undefined);
                            }}>
                            <RemoveIcon fontSize="inherit" />
                        </div>
                    )}
                    <div className={classNames('field-group', 'dropdown')}>
                        <CustomSelect
                            name={`educations[${educationBlockIndex}].school_name_id`}
                            isDisabled={isDisabled}
                            handleScroll={handleSchoolScroll}
                            handleSearch={handleSchoolSearch}
                            options={schools}
                            valueIsString={true}
                            isRequired={isIntern}
                            placeholder={pleaseSelectLabel}
                            label={getMessage('epic.jobs.form.education.school⁠⁠')}
                            labelPropName="text"
                            valuePropName="id"
                            sectionRequiredLabel={sectionRequiredLabel}
                        />
                    </div>
                    <div className={classNames('field-group', 'dropdown')}>
                        <CustomSelect
                            name={`educations[${educationBlockIndex}].degree_id`}
                            isDisabled={isDisabled}
                            options={degrees}
                            valueIsString={true}
                            isRequired={isIntern}
                            placeholder={pleaseSelectLabel}
                            label={getMessage('epic.jobs.form.education.degree⁠⁠')}
                            labelPropName="text"
                            valuePropName="id"
                            sectionRequiredLabel={sectionRequiredLabel}
                        />
                    </div>
                    <div className={classNames('field-group', 'dropdown')}>
                        <CustomSelect
                            name={`educations[${educationBlockIndex}].discipline_id`}
                            isDisabled={isDisabled}
                            options={disciplines}
                            valueIsString={true}
                            isRequired={false}
                            placeholder={pleaseSelectLabel}
                            label={getMessage('epic.jobs.form.education.discipline⁠⁠')}
                            isAutocomplete={true}
                            labelPropName="text"
                            valuePropName="id"
                            sectionRequiredLabel={sectionRequiredLabel}
                        />
                    </div>
                    <div className="field-group">
                        <InputLabel
                            htmlFor={`educations[${educationBlockIndex}].start_date.year`}
                            label={getMessage('epic.jobs.form.education.startDateYear')}
                            required={isIntern}></InputLabel>

                        <Field name={`educations[${educationBlockIndex}].start_date.year`}>
                            {({meta}) => (
                                <div>
                                    {meta.error && meta.touched && (
                                        <InputValidationErrorMsg
                                            error={getMessage(meta.error)}
                                            sectionRequiredLabel={sectionRequiredLabel}
                                        />
                                    )}
                                    <Field
                                        name={`educations[${educationBlockIndex}].start_date.year`}
                                        disabled={isDisabled}
                                        component="input"
                                        maxLength={4}
                                        minLength={4}
                                        size={4}
                                        type="number"
                                        placeholder={getMessage('epic.jobs.form.yyyy')}
                                        min="1900"
                                        max="2199"
                                        className={classNames({
                                            'validate-error': meta.error && meta.touched
                                        })}
                                        validate={(value) => {
                                            return isIntern
                                                ? required(value) || validYear(value)
                                                : validYear(value);
                                        }}
                                    />
                                </div>
                            )}
                        </Field>
                    </div>
                    <div className="field-group">
                        <InputLabel
                            htmlFor={`educations[${educationBlockIndex}].start_date.month`}
                            label={getMessage('epic.jobs.form.education.startDateMonth')}
                            required={false}></InputLabel>
                        <Field name={`educations[${educationBlockIndex}].start_date.month`}>
                            {({meta}) => (
                                <div>
                                    {meta.error && meta.touched && (
                                        <InputValidationErrorMsg
                                            error={getMessage(meta.error)}
                                            sectionRequiredLabel={sectionRequiredLabel}
                                        />
                                    )}
                                    <Field
                                        name={`educations[${educationBlockIndex}].start_date.month`}
                                        disabled={isDisabled}
                                        component="input"
                                        maxLength={2}
                                        minLength={1}
                                        size={2}
                                        type="number"
                                        placeholder={getMessage('epic.jobs.form.mm')}
                                        min="1"
                                        max="12"
                                        className={classNames({
                                            'validate-error': meta.error && meta.touched
                                        })}
                                        validate={(value) => validMonth(value)}
                                    />
                                </div>
                            )}
                        </Field>
                    </div>
                    <div className="field-group">
                        <InputLabel
                            htmlFor={`educations[${educationBlockIndex}].end_date.year`}
                            label={getMessage('epic.jobs.form.education.endDateYear')}
                            required={isIntern}></InputLabel>
                        <Field name={`educations[${educationBlockIndex}].end_date.year`}>
                            {({meta}) => (
                                <div>
                                    {meta.error && meta.touched && (
                                        <InputValidationErrorMsg
                                            error={getMessage(meta.error)}
                                            sectionRequiredLabel={sectionRequiredLabel}
                                        />
                                    )}
                                    <Field
                                        name={`educations[${educationBlockIndex}].end_date.year`}
                                        disabled={isDisabled}
                                        component="input"
                                        maxLength={4}
                                        minLength={4}
                                        size={4}
                                        type="number"
                                        placeholder={getMessage('epic.jobs.form.yyyy')}
                                        min="1900"
                                        max="2199"
                                        className={classNames({
                                            'validate-error': meta.error && meta.touched
                                        })}
                                        validate={(value) => {
                                            return isIntern
                                                ? required(value) || validYear(value)
                                                : validYear(value);
                                        }}
                                    />
                                </div>
                            )}
                        </Field>
                    </div>
                    <div className="field-group">
                        <InputLabel
                            htmlFor={`educations[${educationBlockIndex}].end_date.month`}
                            label={getMessage('epic.jobs.form.education.endDateMonth')}
                            required={false}></InputLabel>
                        <Field name={`educations[${educationBlockIndex}].end_date.month`}>
                            {({meta}) => (
                                <div>
                                    {meta.error && meta.touched && (
                                        <InputValidationErrorMsg
                                            error={getMessage(meta.error)}
                                            sectionRequiredLabel={sectionRequiredLabel}
                                        />
                                    )}
                                    <Field
                                        name={`educations[${educationBlockIndex}].end_date.month`}
                                        disabled={isDisabled}
                                        component="input"
                                        maxLength={2}
                                        minLength={1}
                                        size={2}
                                        type="number"
                                        placeholder={getMessage('epic.jobs.form.mm')}
                                        min="1"
                                        max="12"
                                        className={classNames({
                                            'validate-error': meta.error && meta.touched
                                        })}
                                        validate={(value) => validMonth(value)}
                                    />
                                </div>
                            )}
                        </Field>
                    </div>
                    {/* Dummy Div to make sure odd number columns align */}
                    <div className={classNames('field-group', 'spacer-div')}></div>
                    <VerticalSpacer variant="heading" />
                </>
            )}
        </Styled>
    );
};

export default withLocalization(EducationFormFieldsBlock);
